import { Quaternion, Raycaster, Vector2, Vector3 } from 'three';
import LatLong from '../general/LatLong';

const quat = new Quaternion();
const latLong = new Vector2();
const rayDir = new Vector3();
const rayOrigin = new Vector3();
const tmp = new Vector3();
const raycaster = new Raycaster();

export default class ClickPinController {
  constructor(camera, globe, userPin, domElement) {
    this.container = domElement || document;
    this.camera = camera;
    this.isClick = false;
    this.globe = globe;
    this.pinOffset = 0.02; // basically, the distance from the globe to the pin center
    this.pinRadius = 0.025;
    this.searchRadius = 20; // the radius in which to search for pin hit tests
    this.radius = globe.globeRadius + this.pinOffset;
    this.globeTestRadius = this.radius;
    this._coords = new Vector2();
    this.onPinClicked = null;
    this.onUserPinClicked = null;
    this.userPin = userPin;

    /* this._debugMesh = new Mesh(
      new SphereBufferGeometry(this.pinRadius, 32, 16),
      new MeshBasicMaterial({
        color: 0xff00ff,
        opacity: 0.5,
        transparent: true,
      })
    );
    this.globe.add(this._debugMesh); */
  }

  setPins(groupIDs, data) {
    this._groupIDs = groupIDs;
    this._pinData = data;
  }

  startInteraction() {
    this.container.addEventListener('mousedown', this._onMouseDown);
    this.container.addEventListener('mouseup', this._onMouseUp);
    this.container.addEventListener('mousemove', this._onMouseMove);
    this.container.addEventListener('touchstart', this._onTouchStart);
    this.container.addEventListener('touchend', this._onTouchEnd);
    this.container.addEventListener('touchmove', this._onTouchMove);
  }

  stopInteraction() {
    this.container.removeEventListener('mousedown', this._onMouseDown);
    this.container.removeEventListener('mouseup', this._onMouseUp);
    this.container.removeEventListener('mousemove', this._onMouseMove);
    this.container.removeEventListener('touchstart', this._onTouchStart);
    this.container.removeEventListener('touchend', this._onTouchEnd);
    this.container.removeEventListener('touchmove', this._onTouchMove);
  }

  _onMouseDown = event => {
    this.isClick = true;
    this._coords.x = event.clientX;
    this._coords.y = event.clientY;
  };

  _onMouseUp = () => {
    if (this.isClick) {
      this._onClick();
    }
  };

  _onMouseMove = () => {
    this.isClick = false;
  };

  _onTouchStart = event => {
    this._coords.x = event.touches[0].clientX;
    this._coords.y = event.touches[0].clientY;
    this.isClick = event.touches.length === 1;
  };

  _onTouchEnd = () => {
    if (this.isClick) {
      this._onClick();
    }
  };

  _onTouchMove = () => {
    this.isClick = false;
  };

  _onClick() {
    if (!this._groupIDs || this._groupIDs.length === 0) return;

    this._coords.x = (this._coords.x / window.innerWidth) * 2.0 - 1.0;
    this._coords.y = -(this._coords.y / window.innerHeight) * 2.0 + 1.0;

    raycaster.setFromCamera(this._coords, this.camera);
    const intersects = raycaster.intersectObject(this.userPin, true);
    if (this.onUserPinClicked && intersects && intersects.length > 0) {
      this.onUserPinClicked();
      return;
    }

    rayOrigin.copy(raycaster.ray.origin);
    rayDir.copy(raycaster.ray.direction);

    const p = this._intersectRaySphere(this.globeTestRadius);
    if (!p) return;
    // get the point in the globe's local space to figure out lat/long
    quat.copy(this.globe.quaternion).inverse();
    p.applyQuaternion(quat);
    rayDir.applyQuaternion(quat);
    rayOrigin.applyQuaternion(quat);

    LatLong.fromVector3(p, latLong);
    const pinData = this._findPin(latLong);

    if (pinData) {
      /* LatLong.toVector3(
        pinData.closestPin.location.lat,
        pinData.closestPin.location.long,
        this.globeTestRadius,
        this._debugMesh.position
      ); */

      if (this.onPinClicked)
        this.onPinClicked(pinData.closestPin, pinData.pinID, pinData.closestGroup);
    }
  }

  _intersectRaySphere(radius, center) {
    const t = this._intersectRaySphereT(radius, center);
    if (t == null) return null;
    tmp.copy(rayOrigin).addScaledVector(rayDir, t);
    return tmp;
  }

  _intersectRaySphereT(radius, center) {
    // optimized for sphere origin at 0
    const oc = center ? tmp.subVectors(rayOrigin, center) : rayOrigin;
    const a = rayDir.lengthSq();
    const b = 2.0 * oc.dot(rayDir);
    const c = oc.lengthSq() - radius * radius;
    const det = b * b - 4 * a * c;
    if (det < 0) return null;

    return (-b - Math.sqrt(det)) / (2.0 * a);
  }

  _findPin(latLong) {
    const long = latLong.x;
    const lat = latLong.y;
    let closestT = Number.POSITIVE_INFINITY;
    let closestPin;
    let pinID;
    let closestGroup;
    const maxDistSqr = this.searchRadius * this.searchRadius; // allow a search radius of 20 deg
    for (const groupID of this._groupIDs) {
      const pinGroup = this._pinData[groupID];
      const { pins } = pinGroup;
      for (let i = 0, len = pinGroup.pins.length; i < len; ++i) {
        const pin = pins[i];
        const pinLat = pin.location.lat;
        const pinLong = pin.location.long;
        let dx = pinLat - lat;
        const dy = pinLong - long;
        // shortest angles
        if (dx > 180) dx -= 360;
        else if (dx < -180) dx += 360;

        // TODO: If distSqr < maxDistSqr, test against pin geometry, and keep the one with the closest T
        // Pin matrix should be easily invertible:
        //  (T * Q)^-1 = Q-1 * T-1
        const distSqr = dx * dx + dy * dy;
        if (distSqr < maxDistSqr) {
          // origin of the pin sphere
          LatLong.toVector3(pinLat, pinLong, this.radius, tmp);
          const t = this._intersectRaySphereT(this.pinRadius, tmp);
          if (t > 0 && t < closestT) {
            closestT = t;
            closestPin = pin;
            pinID = i;
            closestGroup = groupID;
          }
        }
      }
    }

    if (!closestPin) return null;

    return {
      closestPin,
      closestGroup,
      pinID,
    };
  }
}
